import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button
} from 'react-bootstrap';
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import * as paymentSlice from "../../../../redux/slices/paymentSlice";
import DataTable from "react-data-table-component";
import { arrowRight } from "../../../../assets/svg/Svg";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { 
  FilePdf, 
  FileXls, 
  MagnifyingGlass, 
  Calendar, 
  Package, 
  CaretDown, 
  CaretUp,
  ArrowsClockwise
} from "@phosphor-icons/react";
import Select from 'react-select';

const RevenueReports = () => {

  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { allPayments, loading } = useSelector((state) => state.paymentSlice);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const paginationPerPage = 10;
  const [selectedPackageType, setSelectedPackageType] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: '0.375rem',
      boxShadow: 'none',
      borderColor: '#ced4da',
      '&:hover': {
        borderColor: '#86b7fe'
      },
      height: '50px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? 'rgba(13, 110, 253, 0.1)' : null,
      color: state.isSelected ? 'white' : 'inherit'
    })
  };

  const getPackageTypeOptions = () => [
    { label: 'Default', value: 'Default' },
    { label: 'Customized', value: 'Customized' }
  ];

  const getPackageOptions = (data) => {
    if (!data) return [];
    const uniquePackages = [...new Set(data.map(item => item?.user_trip?.trip_name))].filter(name => name);
    return uniquePackages.map(name => ({ label: name, value: name }));
  };

  const filterTransactionsByDate = (data) => {
    if (!data || data.length === 0) return [];
    
    return data.filter(transaction => {
      const transactionDate = moment(transaction.created_at);
      return transactionDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
    });
  };

  const calculateStats = (data) => {
    if (!data || data.length === 0) return {
      totalSales: "0.00",
      totalOrders: 0,
      averageOrderValue: "0.00",
      defaultPackages: 0,
      customizedPackages: 0,
      totalCustomers: 0,
      totalEmails: 0
    };
    
    const totalSales = data.reduce((sum, transaction) => sum + (parseFloat(transaction?.amount) || 0), 0);
    const totalOrders = data.length;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
    const defaultPackages = data.filter(item => item?.customized_package == null).length;
    const customizedPackages = data.filter(item => item?.customized_package != null).length;
    
    const totalCustomers = data.filter(item => {
      const fullName = `${item?.user?.first_name || ''} ${item?.user?.last_name || ''}`.trim();
      return fullName && fullName !== '';
    }).length;

    const totalEmails = data.filter(item => item?.user?.email).length;

    return {
      totalSales: totalSales.toFixed(2),
      totalOrders,
      averageOrderValue: averageOrderValue.toFixed(2),
      defaultPackages,
      customizedPackages,
      totalCustomers,
      totalEmails
    };
  };

  const handleSort = (column) => {
    const newDirection = sortField === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(column);
    setSortDirection(newDirection);
  };

  const getSortedData = (data) => {
    if (!data || data.length === 0) return [];
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch(sortField) {
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'customer_name':
          aValue = `${a?.user?.first_name || ''} ${a?.user?.last_name || ''}`.trim().toLowerCase();
          bValue = `${b?.user?.first_name || ''} ${b?.user?.last_name || ''}`.trim().toLowerCase();
          break;
        case 'email':
          aValue = (a?.user?.email || '').toLowerCase();
          bValue = (b?.user?.email || '').toLowerCase();
          break;
        case 'trip_name':
          aValue = (a?.user_trip?.trip_name || '').toLowerCase();
          bValue = (b?.user_trip?.trip_name || '').toLowerCase();
          break;
        case 'amount':
          aValue = parseFloat(a?.amount || 0);
          bValue = parseFloat(b?.amount || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <CaretUp size={16} /> : <CaretDown size={16} />;
  };

  const columns = [
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('created_at')}>
              Created At <SortIcon field="created_at" />
            </div>,
      selector: (row) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
      sortable: true,
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('customer_name')}>
              Customer Name <SortIcon field="customer_name" />
            </div>,
      selector: (row) => {
        const firstName = row?.user?.first_name || "";
        const lastName = row?.user?.last_name || "";
        return firstName || lastName ? `${firstName} ${lastName}`.trim() : "-";
      },
      sortable: true,
    },
    {
      name: "Contact Number",
      selector: (row) => row?.user?.mobile_no || "-",
      sortable: false,
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('email')}>
              Email <SortIcon field="email" />
            </div>,
      selector: (row) => row?.user?.email || "-",
      sortable: true,
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('trip_name')}>
              Trip Name <SortIcon field="trip_name" />
            </div>,
      selector: (row) => row?.user_trip?.trip_name || "-",
      sortable: true,
    },
    {
      name: "Package Type",
      selector: (row) => row?.customized_package == null ? "Default" : "Customized",
      cell: (row) => (
        <span className={`badge ${row?.customized_package == null ? "bg-info" : "bg-primary"}`}>
          {row?.customized_package == null ? "Default" : "Customized"}
        </span>
      ),
      sortable: false,
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('amount')}>
              Payment Amount <SortIcon field="amount" />
            </div>,
      selector: (row) => row?.amount || "-",
      cell: (row) => <span className="fw-bold">₹{row?.amount || "-"}</span>,
      sortable: true,
    },
    {
      name: "Currency",
      selector: (row) => row?.currency || "-",
      sortable: false,
    },
  ];

  useEffect(() => {
    if (!allPayments) return;
    
    const dateFiltered = filterTransactionsByDate(allPayments);
  
    let filtered = dateFiltered;
    
    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        (transaction) => {
          const searchLower = searchTerm.toLowerCase();
          const packageType = transaction?.customized_package == null ? "default" : "customized";
          
          return (
            (transaction?.user?.first_name + ' ' + transaction?.user?.last_name).toLowerCase().includes(searchLower) ||
            transaction?.user?.email?.toLowerCase().includes(searchLower) ||
            transaction?.user?.mobile_no?.includes(searchLower) ||
            transaction?.user_trip?.trip_name?.toLowerCase().includes(searchLower) ||
            packageType.includes(searchLower) ||
            (transaction?.currency || '').toLowerCase().includes(searchLower)
          );
        }
      );
    }

    // Apply customer name filter
    if (customerFilter) {
      filtered = filtered.filter(transaction => {
        const fullName = `${transaction?.user?.first_name || ''} ${transaction?.user?.last_name || ''}`.trim().toLowerCase();
        return fullName.includes(customerFilter.toLowerCase());
      });
    }

    // Apply email filter
    if (emailFilter) {
      filtered = filtered.filter(transaction => 
        transaction?.user?.email?.toLowerCase().includes(emailFilter.toLowerCase())
      );
    }

    // Apply package type filter
    if (selectedPackageType) {
      filtered = filtered.filter(transaction => {
        const isCustomized = transaction?.customized_package != null;
        return selectedPackageType.value === (isCustomized ? 'Customized' : 'Default');
      });
    }

    // Apply package name filter
    if (selectedPackage) {
      filtered = filtered.filter(transaction => 
        transaction?.user_trip?.trip_name === selectedPackage.value
      );
    }
    
    setFilteredTransactions(filtered);
    setCurrentPage(1); 
  }, [allPayments, startDate, endDate, searchTerm, customerFilter, emailFilter, selectedPackageType, selectedPackage]);

  useEffect(() => {
    dispatch(paymentSlice.getPaymentHistory());
  }, [dispatch]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const paginationOptions = {
    rowsPerPageText: "Rows per page:",
    rangeSeparatorText: "of",
    noRowsPerPage: false,
    selectAllRowsItem: false,
    selectAllRowsItemText: "All",
  };

  const customPagination = () => {
    const page = Math.ceil(filteredTransactions.length / paginationPerPage);
    const pageButtons = [];
    const startPage = Math.max(currentPage - 1, 1);
    const endPage = Math.min(currentPage + 1, page);

    pageButtons.push(
      <button
        key={1}
        className={`btn-pagination ${currentPage === 1 ? "active" : ""}`}
        onClick={() => setCurrentPage(1)}
      >
        1
      </button>
    );

    if (startPage > 2) {
      pageButtons.push(
        <span key="ellipsis-start" className="pagination-ellipsis">
          &hellip;
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== page) {
        pageButtons.push(
          <button
            key={i}
            className={`btn-pagination ${currentPage === i ? "active" : ""}`}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </button>
        );
      }
    }
    
    if (endPage < page - 1) {
      pageButtons.push(
        <span key="ellipsis-end" className="pagination-ellipsis">
          &hellip;
        </span>
      );
    }


    if (page !== 1) {
      pageButtons.push(
        <button
          key={page}
          className={`btn-pagination ${currentPage === page ? "active" : ""}`}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </button>
      );
    }

    return (
      <div className="pagination-container mt-5">
        <button
          className="btn-pagination btn-pagi_prev"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {arrowRight}
        </button>
        <span>{pageButtons}</span>
        <button
          className="btn-pagination btn-pagi_next"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === page || page === 0}
        >
          {arrowRight}
        </button>
      </div>
    );
  };

  const sortedData = getSortedData(filteredTransactions);

  const paginatedData = sortedData.slice(
    (currentPage - 1) * paginationPerPage,
    currentPage * paginationPerPage
  );

  const exportToPDF = () => {
    const doc = new jsPDF();
    const stats = calculateStats(filteredTransactions);
  
    // Set a font that supports the rupee symbol
    doc.setFont('helvetica'); // Default font with better unicode support
  
    const pageWidth = doc.internal.pageSize.width;
    const titleText = 'Revenue Report';
    const titleWidth = doc.getStringUnitWidth(titleText) * 16 / doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
  
    // Use 'Rs.' instead of the ₹ symbol for better compatibility
    const rupeesSymbol = 'Rs.';
  
    doc.setFontSize(16);
    doc.text(titleText, titleX, 15);
    doc.setFontSize(10);
    doc.text(`From: ${moment(startDate).format('DD/MM/YYYY')} To: ${moment(endDate).format('DD/MM/YYYY')}`, 14, 25);
  
    doc.setFontSize(12);
    doc.text(`Total Sales: ${rupeesSymbol}${stats.totalSales}`, 14, 35);
    doc.text(`Total Orders: ${stats.totalOrders}`, 14, 42);
    doc.text(`Average Order Value: ${rupeesSymbol}${stats.averageOrderValue}`, 14, 49);
    
    const tableData = filteredTransactions.map(row => [
      moment(row.created_at).format('DD/MM/YYYY HH:mm'),
      `${row?.user?.first_name || ''} ${row?.user?.last_name || ''}`.trim() || '-',
      row?.user?.mobile_no || '-',
      row?.user?.email || '-',
      row?.user_trip?.trip_name || '-',
      row?.customized_package == null ? "Default" : "Customized",
      `${rupeesSymbol}${row?.amount || '-'}`,
      row?.currency || '-'
    ]);
  
    autoTable(doc, {
      head: [columns.map(col => col.name)],
      body: tableData,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { top: 60 },
      didDrawPage: function (data) {
        // Add page numbers at the bottom of each page
        const pageCount = doc.internal.getNumberOfPages();
        const currentPage = data.pageNumber;
        
        // Footer with page number
        doc.setFontSize(8);
        doc.text(
          `Page ${currentPage} of ${pageCount}`, 
          data.settings.margin.left, 
          doc.internal.pageSize.height - 10
        );
      }
    });
  
    doc.save(`Revenue_Report_${moment().format('YYYY-MM-DD')}.pdf`);
  };

  const exportToExcel = () => {
    const stats = calculateStats(filteredTransactions);

    const summaryData = [
      ['Revenue Report'],
      [`From: ${moment(startDate).format('DD/MM/YYYY')} To: ${moment(endDate).format('DD/MM/YYYY')}`],
      [''],
      ['Summary'],
      ['Total Sales', `₹${stats.totalSales}`],
      ['Total Orders', stats.totalOrders],
      ['Average Order Value', `₹${stats.averageOrderValue}`],
      [''],
      ['Transaction Details'],
    ];

    const transactionData = [
      columns.map(col => col.name),
      ...filteredTransactions.map(row => [
        moment(row.created_at).format('DD/MM/YYYY HH:mm'),
        `${row?.user?.first_name || ''} ${row?.user?.last_name || ''}`.trim() || '-',
        row?.user?.mobile_no || '-',
        row?.user?.email || '-',
        row?.user_trip?.trip_name || '-',
        row?.customized_package == null ? "Default" : "Customized",
        row?.amount || '-',
        row?.currency || '-'
      ])
    ];

    const excelData = [...summaryData, ...transactionData];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    XLSX.utils.book_append_sheet(wb, ws, 'Revenue Report');

    XLSX.writeFile(wb, `Revenue_Report_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  const resetFilters = () => {
    setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().endOf('month').format('YYYY-MM-DD'));
    setSearchTerm('');
    setCustomerFilter('');
    setEmailFilter('');
    setSelectedPackageType(null);
    setSelectedPackage(null);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  return (
    <Container fluid className="p-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Revenue Reports</h4>
            <div className="d-flex">
              <Button
                variant="outline-secondary"
                className="me-2 d-flex align-items-center"
                onClick={resetFilters}
              >
                <ArrowsClockwise size={16} className="me-1" /> Reset Filters
              </Button>
              <Button
                variant="outline-primary"
                className="icon-button me-2"
                onClick={exportToExcel}
                disabled={filteredTransactions.length === 0}
                title="Export to Excel"
              >
                <FileXls size={20} className="me-1" /> Excel
              </Button>
              <Button
                variant="outline-danger"
                className="icon-button"
                onClick={exportToPDF}
                disabled={filteredTransactions.length === 0}
                title="Export to PDF"
              >
                <FilePdf size={20} className="me-1" /> PDF
              </Button>
            </div>
          </div>
          
          <Card className="bg-light mb-4">
            <Card.Body>
              <h5 className="mb-3">Filters</h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Calendar size={16} className="me-1" /> From Date
                    </Form.Label>
                    <div className="date-input-wrapper">
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="form-control date-input"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => e.target.showPicker()}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Calendar size={16} className="me-1" /> To Date
                    </Form.Label>
                    <div className="date-input-wrapper">
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="form-control date-input"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => e.target.showPicker()}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <MagnifyingGlass size={16} className="me-1" /> Customer Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Filter by name..."
                      value={customerFilter}
                      onChange={(e) => setCustomerFilter(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <MagnifyingGlass size={16} className="me-1" /> Customer Email
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Filter by email..."
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Package size={16} className="me-1" /> Package Type
                    </Form.Label>
                    <Select
                      isClearable
                      value={selectedPackageType}
                      onChange={setSelectedPackageType}
                      options={getPackageTypeOptions()}
                      placeholder="All Package Types"
                      styles={customSelectStyles}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Package size={16} className="me-1" /> Package Name
                    </Form.Label>
                    <Select
                      isClearable
                      value={selectedPackage}
                      onChange={setSelectedPackage}
                      options={getPackageOptions(allPayments)}
                      placeholder="All Packages"
                      styles={customSelectStyles}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="mb-4 justify-content-around">
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Sales</h6>
                  <h3 className="mb-0 text-primary">₹ {calculateStats(filteredTransactions).totalSales}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Orders</h6>
                  <h3 className="mb-0 text-primary">{calculateStats(filteredTransactions).totalOrders}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Avg. Order Value</h6>
                  <h3 className="mb-0 text-primary">₹ {calculateStats(filteredTransactions).averageOrderValue}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <DataTable
                columns={columns}
                data={paginatedData}
                pagination
                paginationServer
                paginationTotalRows={filteredTransactions.length}
                onChangePage={(page) => setCurrentPage(page)}
                paginationComponentOptions={paginationOptions}
                paginationComponent={customPagination}
                highlightOnHover
                pointerOnHover
                progressPending={loading}
                noDataComponent={
                  <div className="p-5 text-center text-muted">
                    <p className="mb-0">No revenue records found for the selected filters</p>
                  </div>
                }
                progressComponent={
                  <div className="w-100 d-flex p-4">
                    <div className="spinner-border text-primary mx-auto" role="status"></div>
                  </div>
                }
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      borderTopStyle: 'none',
                      borderBottomStyle: 'solid',
                      borderBottomWidth: '1px',
                      borderBottomColor: '#e9ecef',
                    },
                  },
                  headCells: {
                    style: {
                      fontWeight: '600',
                      color: '#495057',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                    },
                  },
                  rows: {
                    style: {
                      minHeight: '55px',
                      fontSize: '14px',
                    },
                  },
                  cells: {
                    style: {
                      paddingLeft: '16px',
                      paddingRight: '16px',
                    },
                  },
                }}
              />
            </Card.Body>
          </Card>
          <div className="text-muted small mb-0">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * paginationPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * paginationPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
          </div>
        </Card.Body>
      </Card>

      <style jsx>{`
        .date-input-wrapper {
          position: relative;
        }
        .date-input {
          width: 100%;
          padding-right: 30px;
        }
        .date-input::-webkit-calendar-picker-indicator {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          width: 20px;
          height: 20px;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 1rem;
        }
        .btn-pagination {
          padding: 0.375rem 0.75rem;
          margin: 0 0.25rem;
          border-radius: 0.25rem;
          border: 1px solid #dee2e6;
          background-color: #fff;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .btn-pagination.active {
          background-color: #0d6efd;
          color: white;
          border-color: #0d6efd;
        }
        .btn-pagination:hover:not(.active) {
          background-color: #f8f9fa;
        }
        .btn-pagination:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .pagination-ellipsis {
          margin: 0 0.25rem;
        }
        .btn-pagi_prev, .btn-pagi_next {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .stats-card {
          transition: transform 0.2s;
        }
        .stats-card:hover {
          transform: translateY(-5px);
          background-color: #f8f9fa;
        }
      `}</style>
    </Container>
  );
};

export default RevenueReports;